import {
  LayoutDashboard,
  Building2,
  ScrollText,
  LifeBuoy,
  Crown,
  Users,
  UserCircle,
  Database,
  FolderKanban,
  Shield,
  type LucideIcon,
} from 'lucide-react';
import { MODULE, MODULE_PERMISSIONS } from '../shared/constants/permissions';

export interface MenuItem {
  id: string;
  name: string;
  link: string;
  icon: LucideIcon;
  requiredPermissions?: string[];
  requiredModule?: string;
  matchPaths?: string[];
  section: 'main' | 'bottom';
}

/**
 * Every gated item quotes MODULE_PERMISSIONS, so what the sidebar shows and what the router lets
 * through are decided by the same permission keys the login response carries. There is no role
 * axis: the server grants a role its permissions, and those alone gate the menu.
 */
const MenuItems: MenuItem[] = [
  // ── Main Navigation ───────────────────────────────────────────────

  {
    id: 'dashboard',
    name: 'Dashboard',
    link: '/dashboard',
    icon: LayoutDashboard,
    section: 'main',
  },

  // Requires CLIENTS_VIEW or CLIENTS_MANAGE
  {
    id: 'clients',
    name: 'Clients',
    link: '/clients',
    icon: Building2,
    section: 'main',
    requiredModule: MODULE.CLIENT,
    requiredPermissions: MODULE_PERMISSIONS[MODULE.CLIENT],
    matchPaths: ['/clients/:id'],
  },

  // Requires USERS_VIEW or USERS_MANAGE
  {
    id: 'users',
    name: 'Users',
    link: '/users',
    icon: Users,
    section: 'main',
    requiredModule: MODULE.USER,
    requiredPermissions: MODULE_PERMISSIONS[MODULE.USER],
    matchPaths: ['/users/:id'],
  },

  // Integrations has no sidebar entry: it is reached through a client or a customer rather than
  // browsed on its own. The `/integrations` routes and their permission guards are still live.

  // Requires TICKETS_VIEW or TICKETS_MANAGE (END_USER sees only own tickets)
  {
    id: 'support-tickets',
    name: 'Support Tickets',
    link: '/support-tickets',
    icon: LifeBuoy,
    section: 'main',
    requiredModule: MODULE.SUPPORT,
    requiredPermissions: MODULE_PERMISSIONS[MODULE.SUPPORT],
    matchPaths: ['/support-tickets/create', '/support-tickets/:id'],
  },

  // Requires BILLING_VIEW or BILLING_MANAGE
  {
    id: 'subscription-plans',
    name: 'Subscriptions & Billing',
    link: '/subscription-plans',
    icon: Crown,
    section: 'main',
    requiredModule: MODULE.SUBSCRIPTION,
    requiredPermissions: MODULE_PERMISSIONS[MODULE.SUBSCRIPTION],
    matchPaths: ['/subscription-plans/:id', '/subscriptions/:id', '/invoices/:id'],
  },

  // Requires EVENT_LOGS_VIEW
  {
    id: 'event-logs',
    name: 'Event Logs',
    link: '/event-logs',
    icon: ScrollText,
    section: 'main',
    requiredModule: MODULE.EVENT_LOG,
    requiredPermissions: MODULE_PERMISSIONS[MODULE.EVENT_LOG],
    matchPaths: ['/event-logs/:id'],
  },

  // Requires PROJECTS_MANAGE — platform projects
  {
    id: 'projects',
    name: 'Project Management',
    link: '/projects',
    icon: FolderKanban,
    section: 'main',
    requiredModule: MODULE.PROJECT,
    requiredPermissions: MODULE_PERMISSIONS[MODULE.PROJECT],
    matchPaths: ['/projects/:id'],
  },

  // Requires ROLES_VIEW or ROLES_MANAGE — roles and the permissions they grant
  {
    id: 'roles',
    name: 'Role Management',
    link: '/roles',
    icon: Shield,
    section: 'main',
    requiredModule: MODULE.ROLE,
    requiredPermissions: MODULE_PERMISSIONS[MODULE.ROLE],
    matchPaths: ['/roles/:id'],
  },

  // Requires SYNC_MANAGE — sync source database configuration
  {
    id: 'source-config',
    name: 'Source Config',
    link: '/source-config',
    icon: Database,
    section: 'main',
    requiredModule: MODULE.SYNC,
    requiredPermissions: MODULE_PERMISSIONS[MODULE.SYNC],
    matchPaths: ['/source-config/:id'],
  },

  // ── Bottom Navigation ─────────────────────────────────────────────

  {
    id: 'profile',
    name: 'My Profile',
    link: '/profile',
    icon: UserCircle,
    section: 'bottom',
  },
];

export default MenuItems;
